import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AllowancePayeBadges } from '@/components/payroll/AllowancePayeBadges';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { canAmendPayroll, canRerunPayroll, getRoleLabel, normalizeUserRole } from '@/lib/roles';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

type BreakdownLine = { name: string; amount: number; type?: string };

type PeriodPayslip = {
  p_id?: string;
  id?: string;
  employee_id?: string;
  firstName?: string;
  lastName?: string;
  employeeId?: string;
  department_name?: string;
  position_title?: string;
  basic_salary?: number;
  total_allowances?: number;
  total_deductions?: number;
  net_salary?: number;
  closing_leave_days?: number;
  allowance_breakdown?: BreakdownLine[];
  deduction_breakdown?: BreakdownLine[];
  notes?: string;
};

type PeriodReviewData = {
  period: string;
  amendable: boolean;
  approval?: { status?: string };
  summary: {
    employee_count: number;
    total_basic: number;
    total_allowances: number;
    total_deductions: number;
    total_net: number;
    total_gross: number;
  };
  payslips: PeriodPayslip[];
};

function payslipId(p: PeriodPayslip): string {
  return String(p.p_id ?? p.id ?? '');
}

function employeeLabel(p: PeriodPayslip): string {
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return name || `Employee #${p.employee_id ?? ''}`;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

const PayrollReview = () => {
  const { user } = useAuth();
  const userRole = normalizeUserRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const periodFromUrl = searchParams.get('period') ?? new Date().toISOString().substring(0, 7);
  const [period, setPeriod] = useState(periodFromUrl);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState<PeriodReviewData | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<PeriodPayslip | null>(null);
  const [saving, setSaving] = useState(false);
  const [editBasic, setEditBasic] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editAllowances, setEditAllowances] = useState<BreakdownLine[]>([]);
  const [editDeductions, setEditDeductions] = useState<BreakdownLine[]>([]);
  const [recalcPaye, setRecalcPaye] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);

  const loadReview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getPayrollPeriodReview(period, user?.cid ?? null);
      const data = (res as { data?: PeriodReviewData })?.data ?? (res as PeriodReviewData);
      setReview(data);
    } catch {
      toast.error('Failed to load payroll review');
      setReview(null);
    } finally {
      setLoading(false);
    }
  }, [period, user?.cid]);

  useEffect(() => {
    setPeriod(periodFromUrl);
  }, [periodFromUrl]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const approvalStatus = review?.approval?.status;
  const amendable = review?.amendable && canAmendPayroll(userRole, approvalStatus);
  const councilId = user?.cid != null ? Number(user.cid) : 0;
  const canRerun = canRerunPayroll(userRole, approvalStatus) && councilId > 0;

  const filteredPayslips = useMemo(() => {
    const list = review?.payslips ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const label = employeeLabel(p).toLowerCase();
      const empNo = String(p.employeeId ?? p.employee_id ?? '').toLowerCase();
      const dept = String(p.department_name ?? '').toLowerCase();
      return label.includes(q) || empNo.includes(q) || dept.includes(q);
    });
  }, [review?.payslips, search]);

  const openEdit = (p: PeriodPayslip) => {
    setEditing(p);
    setEditBasic(String(num(p.basic_salary)));
    setEditNotes(String(p.notes ?? ''));
    setEditAllowances(
      (p.allowance_breakdown ?? []).map((a) => ({
        name: a.name,
        amount: num(a.amount),
        type: a.type,
      }))
    );
    setEditDeductions(
      (p.deduction_breakdown ?? []).map((d) => ({
        name: d.name,
        amount: num(d.amount),
        type: d.type,
      }))
    );
    setRecalcPaye(false);
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    setSearchParams({ period: value });
  };

  const handleRerunPayroll = async () => {
    if (!user?.id || !period) return;
    const cid = user?.cid != null ? Number(user.cid) : 0;
    if (!cid || cid <= 0) {
      toast.error('Council context (cid) is required to rerun payroll. Please log in again or contact an administrator.');
      return;
    }
    setRerunning(true);
    try {
      const result = await apiService.rerunPayrollPeriod({
        period,
        role: userRole,
        user_id: Number(user.id),
        cid,
      });
      const payload = (result as { data?: unknown })?.data;
      const rows = Array.isArray(payload) ? payload : [];
      setRerunDialogOpen(false);
      const errors = rows.filter((r: { error?: string }) => r?.error);
      const processed = rows.length - errors.length;
      if (errors.length > 0) {
        toast.warning(
          `Payroll rerun finished: ${processed} processed, ${errors.length} skipped or failed.`
        );
      } else {
        toast.success(`Payroll for ${period} has been rerun for ${processed || rows.length} employee(s).`);
      }
      await loadReview();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Failed to rerun payroll')
          : 'Failed to rerun payroll';
      toast.error(message);
    } finally {
      setRerunning(false);
    }
  };

  const handleSaveAmendment = async () => {
    if (!editing || !user?.id) return;
    const id = payslipId(editing);
    if (!id) {
      toast.error('Invalid payslip');
      return;
    }
    setSaving(true);
    try {
      await apiService.amendPayslip({
        payslip_id: id,
        role: userRole,
        user_id: Number(user.id),
        basic_salary: num(editBasic),
        notes: editNotes,
        allowances: editAllowances.filter((a) => a.name.trim()),
        deductions: editDeductions.filter((d) => d.name.trim()),
        recalculate_paye: recalcPaye,
      });
      toast.success('Payslip updated');
      setEditing(null);
      await loadReview();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to save amendment';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateLine = (
    lines: BreakdownLine[],
    setLines: (v: BreakdownLine[]) => void,
    index: number,
    field: 'name' | 'amount',
    value: string
  ) => {
    const next = [...lines];
    if (field === 'amount') {
      next[index] = { ...next[index], amount: num(value) };
    } else {
      next[index] = { ...next[index], name: value };
    }
    setLines(next);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Button variant="ghost" size="sm" className="mb-2 -ml-2" asChild>
              <Link to="/payroll">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to payroll
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">Pre-approval payroll review</h1>
            <p className="text-gray-500">
              Review all payslips for a period and amend amounts before sign-off.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="reviewPeriod">Period</Label>
            <Input
              id="reviewPeriod"
              type="month"
              value={period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="w-40"
            />
            {canRerun && (
              <AlertDialog open={rerunDialogOpen} onOpenChange={setRerunDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" disabled={rerunning || loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${rerunning ? 'animate-spin' : ''}`} />
                    Rerun entire payroll
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Rerun payroll for {period}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will delete all payslips for this period for your council only, reset approval
                      steps to draft, and reprocess every active employee using current salary, allowances,
                      and deductions (including updated PAYE/ZRA exempt settings from Settings).
                      Manual payslip edits in Amend are replaced on rerun. To make an allowance ZRA exempt,
                      turn off PAYE under Settings → Allowances first, then rerun. This cannot be done
                      after final approval.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={rerunning}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={rerunning}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRerunPayroll();
                      }}
                    >
                      {rerunning ? 'Rerunning…' : 'Yes, rerun payroll'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {approvalStatus && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            Approval status:
            <Badge variant="outline">{approvalStatus}</Badge>
            {!amendable && approvalStatus === 'approved' && (
              <span className="text-amber-700">This period is locked for amendments and reruns.</span>
            )}
            {canRerun && (
              <span className="text-gray-500">
                Use rerun if processed figures are wrong after updating employee salary data or allowance PAYE settings in Settings.
              </span>
            )}
          </div>
        )}

        {review?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Employees</CardDescription>
                <CardTitle className="text-xl">{review.summary.employee_count}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total basic</CardDescription>
                <CardTitle className="text-lg">{formatCurrency(review.summary.total_basic)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Allowances</CardDescription>
                <CardTitle className="text-lg">{formatCurrency(review.summary.total_allowances)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Deductions</CardDescription>
                <CardTitle className="text-lg">{formatCurrency(review.summary.total_deductions)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Net pay</CardDescription>
                <CardTitle className="text-lg">{formatCurrency(review.summary.total_net)}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Employee payslips</CardTitle>
              <CardDescription>
                {amendable
                  ? `You can amend as ${getRoleLabel(userRole)} until final approval.`
                  : 'Read-only view for this period.'}
              </CardDescription>
            </div>
            <Input
              placeholder="Search name, ID, department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-payroll-primary" />
              </div>
            ) : filteredPayslips.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No payslips for this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Dept / position</TableHead>
                      <TableHead>Allowances (PAYE)</TableHead>
                      <TableHead className="text-right">Leave bal.</TableHead>
                      <TableHead className="text-right">Basic</TableHead>
                      <TableHead className="text-right">Allowances</TableHead>
                      <TableHead className="text-right">Deductions</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      {amendable && <TableHead className="w-20" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayslips.map((p) => (
                      <TableRow key={payslipId(p)}>
                        <TableCell>
                          <div className="font-medium">{employeeLabel(p)}</div>
                          <div className="text-xs text-gray-500">{p.employeeId ?? p.employee_id}</div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {p.department_name ?? '—'}
                          {p.position_title ? ` · ${p.position_title}` : ''}
                        </TableCell>
                        <TableCell>
                          <AllowancePayeBadges payslip={p} compact />
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {num(p.closing_leave_days) > 0 ? `${num(p.closing_leave_days).toFixed(1)} days` : '—'}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(num(p.basic_salary))}</TableCell>
                        <TableCell className="text-right">{formatCurrency(num(p.total_allowances))}</TableCell>
                        <TableCell className="text-right">{formatCurrency(num(p.total_deductions))}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(num(p.net_salary))}</TableCell>
                        {amendable && (
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => openEdit(p)} aria-label="Edit payslip">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Sheet open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Amend payslip</SheetTitle>
            <SheetDescription>
              {editing ? employeeLabel(editing) : ''} — {period}. Changes here are for this payslip only.
              To change PAYE/ZRA treatment for an allowance council-wide, turn off PAYE in Settings → Allowances,
              then use Rerun entire payroll.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 py-6">
            <div className="space-y-2">
              <Label htmlFor="editBasic">Basic salary</Label>
              <Input
                id="editBasic"
                type="number"
                min={0}
                step="0.01"
                value={editBasic}
                onChange={(e) => setEditBasic(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editNotes">Notes</Label>
              <Input id="editNotes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Allowances</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditAllowances([...editAllowances, { name: '', amount: 0, type: 'fixed' }])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {editAllowances.map((line, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      placeholder="Name"
                      value={line.name}
                      onChange={(e) => updateLine(editAllowances, setEditAllowances, i, 'name', e.target.value)}
                      className="flex-1"
                    />
                    {line.type === 'taxable' && (
                      <Badge variant="outline" className="text-xs shrink-0">PAYE</Badge>
                    )}
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={line.amount}
                      onChange={(e) => updateLine(editAllowances, setEditAllowances, i, 'amount', e.target.value)}
                      className="w-28"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditAllowances(editAllowances.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Deductions</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDeductions([...editDeductions, { name: '', amount: 0, type: 'fixed' }])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {editDeductions.map((line, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      placeholder="Name"
                      value={line.name}
                      onChange={(e) => updateLine(editDeductions, setEditDeductions, i, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={line.amount}
                      onChange={(e) => updateLine(editDeductions, setEditDeductions, i, 'amount', e.target.value)}
                      className="w-28"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditDeductions(editDeductions.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="recalcPaye" checked={recalcPaye} onCheckedChange={(v) => setRecalcPaye(!!v)} />
              <Label htmlFor="recalcPaye" className="text-sm font-normal cursor-pointer">
                Recalculate PAYE from basic + taxable allowances
              </Label>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAmendment} disabled={saving}>
              {saving ? 'Saving…' : 'Save amendment'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </DashboardLayout>
  );
};

export default PayrollReview;
